import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Eye, Download, Loader2, Globe } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

/**
 * ShareLinkActivityDrawer — detailed per-link activity log for a share link.
 * Shows the individual VIEW and DOWNLOAD events (each with timestamp + IP +
 * user-agent) so operators have a chain-of-custody trail of who opened or
 * saved the evidence and when.
 *
 * Backend: GET /api/media/shared-evidence/links/{shareId}/audit
 */
const fmtAt = (at) => {
  try { return format(new Date(at), 'PPp'); } catch { return at || '—'; }
};

const ActivitySection = ({ icon: Icon, title, count, entries, emptyText, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-slate-500" />
      <span className="text-sm font-medium">{title}</span>
      <Badge variant="outline" data-testid={`${testid}-count`}>{count}</Badge>
    </div>
    {(!entries || entries.length === 0) ? (
      <p className="text-xs text-muted-foreground italic">{emptyText}</p>
    ) : (
      <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
        {entries.map((e, i) => (
          <div key={`${e.at}-${i}`} className="flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-xs" title={e.ua || ''}>
            <span className="font-medium whitespace-nowrap">{fmtAt(e.at)}</span>
            <span className="flex items-center gap-1 text-muted-foreground font-mono">
              <Globe className="w-3 h-3" /> {e.ip || 'unknown'}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const ShareLinkActivityDrawer = ({ open, onOpenChange, shareId, label }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !shareId) return;
    let cancelled = false;
    // Open-driven fetch: set loading flags then load — correct effect pattern.

    setLoading(true);
    setError(null);
    setData(null);
    api.get(`/media/shared-evidence/links/${shareId}/audit`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.detail || 'Failed to load link activity'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, shareId, api]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-activity-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4" /> Link activity</DialogTitle>
          <DialogDescription>
            {label || 'Timestamped view & download log (with IP address) for chain-of-custody.'}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground" data-testid="share-activity-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading activity…
          </div>
        ) : error ? (
          <p className="text-sm text-rose-600 py-6 text-center" data-testid="share-activity-error">{error}</p>
        ) : data ? (
          <div className="space-y-4">
            <ActivitySection
              icon={Eye}
              title="Views"
              count={data.view_count || 0}
              entries={data.views}
              emptyText="No views recorded yet."
              testid="share-activity-views"
            />
            <ActivitySection
              icon={Download}
              title="Downloads"
              count={data.download_count || 0}
              entries={data.downloads}
              emptyText={data.allow_download ? 'No downloads recorded yet.' : 'Downloading is not enabled for this link.'}
              testid="share-activity-downloads"
            />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ShareLinkActivityDrawer;
